import type Immutable from 'immutable';
import { createMigration } from './utils';

/**
 * Locations that may contain assets. The ones marked as "NO" can never be an asset that can contain circle graphic; otherwise, they can all potentially have circle graphic(s):
 * 0. root->audioAssets->[str]                              NO (audio asset)
 * 1. root->sceneGroups->[i]->mediaAsset
 * 2. root->sceneGroups->[i]->voiceoverAsset                NO (audio asset)
 * 3. root->sceneGroups->[i]->captions                      NO (captions manager)
 * 4. root->sceneGroups->[i]->captions->asset               NO (caption asset | fancy caption asset)
 * 5. root->slides->[i]->children->[i]
 * 6. root->slides->[i]->stickers->[i]
 * 7. root->slides->[i]->subslides->[i]->children->[i]
 */

function migration_getCircleOffsetByRadiusAndStrokeWidth(radius: number, strokeWidth: number) {
    return radius + strokeWidth * 0.5;
}

function migrateGraphic(
    someParentNode: Immutable.Map<string, any>,
    pathFromParentNode: (string | number)[],
) {
    const graphic = someParentNode.getIn(pathFromParentNode) as
        | Immutable.Map<string, any>
        | undefined;
    if (
        graphic &&
        graphic.has('graphic') &&
        graphic.get('graphic') === 'circle' &&
        graphic.has('shape')
    ) {
        pathFromParentNode.push('shape');
        const shape = graphic.get('shape') as Immutable.Map<string, any> | undefined;
        const strokeWidth = shape.has('strokeWidth') ? (shape.get('strokeWidth') as number) : 0;
        const radius = shape.has('radius') ? (shape.get('radius') as number) : 0;
        const offset = migration_getCircleOffsetByRadiusAndStrokeWidth(radius, strokeWidth);
        if (shape.has('positionX')) {
            someParentNode.setIn(
                [...pathFromParentNode, 'positionX'],
                (shape.get('positionX') as number) - offset,
            );
        }
        if (shape.has('positionY')) {
            someParentNode.setIn(
                [...pathFromParentNode, 'positionY'],
                (shape.get('positionY') as number) - offset,
            );
        }
        pathFromParentNode.pop();
    }
}

function migrateAsset(
    someParentNode: Immutable.Map<string, any>,
    pathFromParentNode: (string | number)[],
) {
    const asset = someParentNode.getIn(pathFromParentNode) as
        | Immutable.Map<string, any>
        | undefined;
    if (asset && asset.has('colorGraphics')) {
        const graphics = asset.get('colorGraphics') as Immutable.List<any> | undefined;
        pathFromParentNode.push('colorGraphics');
        graphics.forEach((_, ind) => {
            pathFromParentNode.push(ind);
            migrateGraphic(someParentNode, pathFromParentNode);
            pathFromParentNode.pop();
        });
        pathFromParentNode.pop();
    }
}

export const migrationTo9_232 = createMigration({
    migrateVideo(video) {
        return video.withMutations((map) => {
            map.get('sceneGroups').forEach((sceneGroup, i) => {
                map.setIn(['sceneGroups', i], this.migrateSceneGroup(sceneGroup));
            });
            map.get('slides').forEach((scene, i) => {
                map.setIn(['slides', i], this.migrateScene(scene));
            });
        });
    },
    migrateSceneGroup(_sg) {
        return _sg.withMutations((sceneGroup) => {
            // 1. root->sceneGroups->[i]->mediaAsset
            if (sceneGroup.has('mediaAsset')) {
                migrateAsset(sceneGroup, ['mediaAsset']);
            }
        });
    },
    migrateScene(_s) {
        return _s.withMutations((scene) => {
            // 5. root->slides->[i]->children->[i]
            if (scene.has('children')) {
                const children = scene.get('children') as Immutable.List<any>;
                children.forEach((_, i) => {
                    migrateAsset(scene, ['children', i]);
                });
            }
            // 6. root->slides->[i]->stickers->[i]
            if (scene.has('stickers')) {
                const stickers = scene.get('stickers') as Immutable.List<any>;
                stickers.forEach((_, i) => {
                    migrateAsset(scene, ['stickers', i]);
                });
            }
            // 7. root->slides->[i]->subslides->[i]->children->[i]
            if (scene.has('subslides')) {
                const subslides = scene.get('subslides') as Immutable.List<any>;
                subslides.forEach((subslide: Immutable.Map<string, any>, i) => {
                    if (subslide.has('children')) {
                        const children = subslide.get('children') as Immutable.List<any>;
                        children.forEach((_, j) => {
                            migrateAsset(scene, ['subslides', i, 'children', j]);
                        });
                    }
                });
            }
        });
    },
});
